import React, { useState } from 'react';
import Modal from '../UI/Modal';
import { CVAnalysisResult } from './types';
import Button from '../UI/Button';
import { BiBarChart, BiInfoCircle } from "react-icons/bi";
import {
  FaArrowRight, FaBook, FaBriefcase, FaChartLine,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp, FaClipboardCheck,
  FaExclamationCircle, FaGraduationCap,
  FaLightbulb, FaStar,
  FaThumbsUp, FaTimesCircle
} from "react-icons/fa";

interface CVAnalysisModalProps {
  show: boolean;
  onHide: () => void;
  analysisData: CVAnalysisResult | null;
}

const CVAnalysisModal: React.FC<CVAnalysisModalProps> = ({ show, onHide, analysisData }) => {
  const [expandedRecommendation, setExpandedRecommendation] = useState<number | null>(null);

  if (!analysisData?.data?.analysis) {
    return null;
  }

  const { analysis } = analysisData.data;

  // Toggle recommendation accordion
  const toggleRecommendation = (index: number) => {
    setExpandedRecommendation(expandedRecommendation === index ? null : index);
  };

  // Get score color based on percentage
  const getScoreColor = (score: number, maxScore: number = 5) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-yellow-500';
    if (percentage >= 40) return 'bg-blue-500';
    return 'bg-red-500';
  };

  // Get readiness badge color
  const getReadinessBadge = (readiness: string) => {
    switch (readiness.toLowerCase()) {
      case 'siap':
        return 'bg-green-500 text-white';
      case 'hampir siap':
        return 'bg-yellow-500 text-black';
      case 'belum siap':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="gradient-primary text-white">
        <Modal.Title>
          <FaChartLine className="mr-2 text-white" />
          <span className='text-white'>Hasil Analisis ATS CV</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {/* Executive Summary */}
        <div className="grid grid-cols-1 mb-4">
          <div className="border border-primary rounded-lg">
            <div className="bg-primary text-white p-3 rounded-t-lg">
              <h5 className="mb-0 flex items-center">
                <FaClipboardCheck className="mr-2" />
                Ringkasan Eksekutif
              </h5>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <h6 className="text-gray-500 mb-2">Status ATS</h6>
                  <span
                    className={`inline-block px-3 py-2 rounded text-sm font-medium ${getReadinessBadge(
                      analysis.executive_summary.ats_readiness
                    )}`}
                  >
                    {analysis.executive_summary.ats_readiness}
                  </span>
                </div>
                <div className="text-center">
                  <h6 className="text-gray-500 mb-2">Target User</h6>
                  <p className="font-bold mb-0">
                    {analysis.executive_summary.target_user}
                  </p>
                </div>
                <div className="text-center">
                  <h6 className="text-gray-500 mb-2">Career Match</h6>
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-6">
                      <div
                        className={`h-6 rounded-full flex items-center justify-center text-white text-sm font-medium ${getScoreColor(
                          analysis.executive_summary.career_match_percentage,
                          100
                        )}`}
                        style={{
                          width: `${analysis.executive_summary.career_match_percentage}%`,
                        }}
                      >
                        {analysis.executive_summary.career_match_percentage}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="grid grid-cols-1 mb-4">
          <div className="border rounded-lg">
            <div className="bg-blue-500 text-white p-3 rounded-t-lg">
              <h5 className="mb-0 flex items-center">
                <BiBarChart className="mr-2" />
                Breakdown Skor (Total: {analysis.score_breakdown.total_score}/100)
              </h5>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {Object.entries(analysis.score_breakdown).map(
                  ([key, value]) => {
                    if (key === "total_score") return null;
                    const scoreItem = value as any;
                    const sectionNames: Record<string, string> = {
                      ats_format: "Format ATS",
                      career_clarity: "Kejelasan Karier",
                      profile_branding: "Branding Profil",
                      hard_skills: "Hard Skills",
                      soft_skills: "Soft Skills",
                      experience: "Pengalaman",
                      achievements: "Prestasi",
                    };

                    return (
                      <div key={key} className="border border-gray-200 rounded-lg p-4 text-center h-full">
                        <h6 className="text-gray-500 mb-2">
                          {sectionNames[key]}
                        </h6>
                        <div className="mb-2">
                          <span
                            className={`inline-block px-2 py-1 rounded text-white text-sm font-medium ${getScoreColor(
                              scoreItem.score
                            )}`}
                          >
                            {scoreItem.score}/5
                          </span>
                        </div>
                        <small className="text-gray-500">
                          Bobot: {scoreItem.weight}% | Nilai: {scoreItem.weighted_value}
                        </small>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Score Interpretation */}
        <div className="grid grid-cols-1 mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h6 className="flex items-center text-blue-800 font-semibold mb-2">
              <BiInfoCircle className="mr-2" />
              Interpretasi Skor
            </h6>
            <strong className="text-blue-900">{analysis.score_interpretation.category}</strong>
            <p className="mb-0 mt-2 text-blue-800">
              {analysis.score_interpretation.description}
            </p>
          </div>
        </div>

        {/* Qualitative Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="border border-green-200 rounded-lg h-full">
            <div className="bg-green-500 text-white p-3 rounded-t-lg">
              <h6 className="mb-0 flex items-center">
                <FaThumbsUp className="mr-2" />
                Kekuatan CV
              </h6>
            </div>
            <div className="p-4">
              {analysis.qualitative_analysis.strengths.length > 0 ? (
                <ul className="space-y-2">
                  {analysis.qualitative_analysis.strengths.map(
                    (strength, index) => (
                      <li key={index} className="flex items-start">
                        <FaCheckCircle className="mr-2 text-green-500 mt-1 flex-shrink-0" />
                        <span>{strength}</span>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <p className="text-gray-500">
                  Tidak ada kekuatan yang teridentifikasi.
                </p>
              )}
            </div>
          </div>
          <div className="border border-yellow-200 rounded-lg h-full">
            <div className="bg-yellow-500 text-black p-3 rounded-t-lg">
              <h6 className="mb-0 flex items-center">
                <FaExclamationCircle className="mr-2" />
                Area Perbaikan
              </h6>
            </div>
            <div className="p-4">
              {analysis.qualitative_analysis.improvement_areas.length > 0 ? (
                <ul className="space-y-2">
                  {analysis.qualitative_analysis.improvement_areas.map(
                    (area, index) => (
                      <li key={index} className="flex items-start">
                        <FaExclamationCircle className="mr-2 text-yellow-500 mt-1 flex-shrink-0" />
                        <span>{area}</span>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <p className="text-gray-500">
                  Tidak ada area perbaikan yang teridentifikasi.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Concrete Recommendations */}
        <div className="grid grid-cols-1 mb-4">
          <div className="border rounded-lg">
            <div className="bg-primary text-white p-3 rounded-t-lg">
              <h5 className="mb-0 flex items-center">
                <FaLightbulb className="mr-2" />
                Rekomendasi Konkret
              </h5>
            </div>
            <div className="p-4">
              <div className="space-y-2">
                {analysis.concrete_recommendations.map((rec, index) => (
                  <div key={index} className="border rounded-lg">
                    <div className="p-0">
                      <button
                        className="w-full text-left p-3 hover:bg-gray-50 transition-colors rounded-lg"
                        type="button"
                        onClick={() => toggleRecommendation(index)}
                      >
                        <div className="flex justify-between items-center">
                          <strong className="text-gray-700">{rec.section}</strong>
                          {expandedRecommendation === index ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                      </button>
                    </div>
                    {expandedRecommendation === index && (
                      <div className="border-t p-4 animate-fadeIn">
                        <div className="mb-3">
                          <h6 className="text-red-600 flex items-center mb-2">
                            <FaTimesCircle className="mr-2" />
                            Masalah:
                          </h6>
                          <p className="mb-0 text-gray-600">{rec.issue}</p>
                        </div>
                        <div>
                          <h6 className="text-green-600 flex items-center mb-2">
                            <FaCheckCircle className="mr-2" />
                            Saran:
                          </h6>
                          <p className="mb-0 text-gray-600">
                            {rec.suggestion}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Development Recommendations */}
        <div className="grid grid-cols-1">
          <div className="border rounded-lg">
            <div className="bg-gray-500 text-white p-3 rounded-t-lg">
              <h5 className="mb-0 flex items-center">
                <FaGraduationCap className="mr-2" />
                Rekomendasi Pengembangan
              </h5>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h6 className="text-primary flex items-center mb-3">
                    <FaStar className="mr-2" />
                    Skills Prioritas
                  </h6>
                  <ul className="space-y-1">
                    {analysis.development_recommendations.priority_skills.map(
                      (skill, index) => (
                        <li key={index} className="flex items-center">
                          <FaArrowRight className="mr-2 text-primary flex-shrink-0" />
                          <span>{skill}</span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
                <div>
                  <h6 className="text-green-600 flex items-center mb-3">
                    <FaBook className="mr-2" />
                    Pelatihan yang Disarankan
                  </h6>
                  <ul className="space-y-1">
                    {analysis.development_recommendations.suggested_training.map(
                      (training, index) => (
                        <li key={index} className="flex items-center">
                          <FaArrowRight className="mr-2 text-green-600 flex-shrink-0" />
                          <span>{training}</span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
                <div>
                  <h6 className="text-blue-500 flex items-center mb-3">
                    <FaBriefcase className="mr-2" />
                    Saran Portfolio
                  </h6>
                  <ul className="space-y-1">
                    {analysis.development_recommendations.portfolio_suggestions.map(
                      (suggestion, index) => (
                        <li key={index} className="flex items-center">
                          <FaArrowRight className="mr-2 text-blue-500 flex-shrink-0" />
                          <span>{suggestion}</span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <small className="text-gray-500 mr-auto">
          Dianalisis pada:{" "}
          {new Date(analysisData.data.analyzed_at).toLocaleString("id-ID")}
        </small>
        <Button onClick={onHide} secondary>
          Tutup
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CVAnalysisModal;